<template>
  <div
    class="image-cropper"
    :class="{ 'image-cropper--has-image': image.src.length}"
  >
    <div
      class="cropper-wrapper"
      v-show="image.src.length"
    >
      <cropper
        ref="cropper"
        :class="'cropper' + ' ' + extraClass"
        :src="image.src"
        image-restriction="stencil"
        :stencil-props="stencilSettings"
        :resize-image="resizeSettings"
        :min-height="minHeight"
        :min-width="minWidth"
        @change="onChange"
      />

      <div
        class="reset-button"
        title="Remove Image"
        @click="reset()"
      >
        <img
          :src="require('../../svg/reset.svg')"
          alt="Remove image"
        >
      </div>

      <div
        v-if="image.type"
        class="file-type"
      >
        {{ image.type }}
      </div>
    </div>

    <div
      class="buttons-wrapper"
    >
      <button
        v-show="image.src.length === 0"
        class="btn"
        @click="$refs.file.click()"
      >
        <input
          ref="file"
          type="file"
          accept="image/*"
          @click="$refs.file.value = ''"
          @change="loadImage($event)"
        >
        {{ loadImageButtonText }}
      </button>
      <button
        v-show="image.src.length > 0"
        :disabled="cropButtonSettings.disabled"
        class="btn"
        @click.prevent="crop()"
      >
        {{ cropButtonSettings.innerText }}
      </button>
    </div>
  </div>
</template>

<script>
import {Cropper} from 'vue-advanced-cropper'
import 'vue-advanced-cropper/dist/style.css'
import 'vue-advanced-cropper/dist/theme.compact.scss'

export default {
  components: {
    Cropper,
  },
  props: {
    aspectRatio: {
      type: Number,
      default: 3 / 4,
    },
    minWidth: {
      type: Number,
      default: null,
    },
    minHeight: {
      type: Number,
      default: null,
    },
    loadImageButtonText: {
      type: String,
      default: 'Upload header image',
    },
    cropButtonSettings: {
      type: Object,
      default: () => {
        return {
          innerText: 'Crop',
          disabled: false,
        }
      },
    },
    extraClass: {
      type: String,
      default: '',
    },
  },
  data: () => {
    return {
      image: {
        src: '',
        type: null,
      },
      result: {
        coordinates: null,
        image: null,
      },
      resizeSettings: {
        touch: true,
        wheel: {
          ratio: 0.01,
        },
        adjustStencil: false,
      },
    }
  },
  computed: {
    stencilSettings: function () {
      return {
        handlers: {},
        movable: true,
        scalable: true,
        aspectRatio: this.aspectRatio,
      }
    },
  },
  destroyed() {
    if (!this.image.src) {
      return
    }

    URL.revokeObjectURL(this.image.src)
  },
  methods: {
    crop() {
      const {canvas} = this.$refs.cropper.getResult()

      canvas.toBlob((blob) => {
        this.$emit('crop', blob)
      }, this.image.type)
    },

    reset() {
      this.image = {
        src: '',
        type: null,
      }

      this.$emit('reset')
    },

    onChange({coordinates, image}) {
      this.result = {
        coordinates,
        image,
      }
    },

    loadImage(event) {
      const {files} = event.target

      if (!files || files.length < 1) {
        return
      }

      if (this.image.src) {
        URL.revokeObjectURL(this.image.src)
      }

      const blob = URL.createObjectURL(files[0])
      const reader = new FileReader()

      reader.onload = (e) => {
        this.image = {
          src: blob,
          type: this.getMimeType(e.target.result, files[0].type),
        }
      }

      reader.readAsArrayBuffer(files[0])
    },

    getMimeType(file, fallback = null) {
      const byteArray = new Uint8Array(file).subarray(0, 4)

      let header = ''

      for (let i = 0; i < byteArray.length; i++) {
        header += byteArray[i].toString(16)
      }

      switch (header) {
        case '89504e47':
          return 'image/png'
        case '47494638':
          return 'image/gif'
        case 'ffd8ffe0':
        case 'ffd8ffe1':
        case 'ffd8ffe2':
        case 'ffd8ffe3':
        case 'ffd8ffe8':
          return 'image/jpeg'
        default:
          return fallback
      }
    },
  },
}
</script>

<style lang="scss">
.image-cropper--has-image {
  margin-top: 20px;
  margin-bottom: 20px;
}

.image-cropper {
  width: 480px;
  user-select: none;

  > .cropper-wrapper {
    position: relative;

    > .cropper {
      border: solid 1px #eee;
      min-height: 300px;
      max-height: 500px;
      width: 100%;
    }

    .reset-button {
      background: rgba(253, 205, 11, 0.50);
      position: absolute;
      right: 20px;
      bottom: 20px;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      height: 42px;
      width: 42px;
      transition: background 0.5s;

      &:hover {
        background: rgba(253, 205, 11, 1);
      }
    }

    > .file-type {
      position: absolute;
      top: 20px;
      left: 20px;
      background: #0d0d0d;
      border-radius: 5px;
      padding: 0 10px 2px;
      font-size: 12px;
      color: white;
    }
  }

  > .buttons-wrapper {
    display: flex;
    justify-content: space-between;
    margin-top: 17px;

    > .btn {
      > input {
        display: none;
      }
    }
  }
}
</style>